import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { BrandMark } from "../components/Brand";
import StatusBadge from "../components/StatusBadge";
import { api, STATUS_LABELS, STATUS_ORDER, formatCurrency, formatDate } from "../lib/api";
import { getAdmin, fetchAdminMe } from "../lib/auth";
import { ArrowLeft, FileText, Plus, Mail, Trash2, Send, Upload, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { FundCaseDialog } from "../components/admin/FundCaseDialog";
import CommunicationsTab from "../components/communications/CommunicationsTab";
import ActivityTimeline from "../components/communications/ActivityTimeline";
import VerificationBanner from "../components/communications/VerificationBanner";
import ServiceAreaReviewBanner from "../components/admin/ServiceAreaReviewBanner";

const errMsg = (e, fallback) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fallback);

export default function AdminAppDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const admin = getAdmin();
  const [app, setApp] = useState(null);
  const [note, setNote] = useState("");
  const [recommendation, setRecommendation] = useState("");
  const [feeRate, setFeeRate] = useState("");
  const [tierMonths, setTierMonths] = useState("");
  const [maxTiers, setMaxTiers] = useState("");
  const [agreementName, setAgreementName] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [fundOpen, setFundOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { await fetchAdminMe(); } catch {
        try { localStorage.removeItem("instabridge_admin"); } catch (err) { console.error("clear-admin-state", err); }
        if (!cancelled) nav("/login", { replace: true });
        return;
      }
      if (!cancelled) await load();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [id]);

  const load = async () => {
    try {
      const { data } = await api.get(`/applications/${id}`);
      setApp(data);
      setRecommendation(data.funding_recommendation ?? "");
      setFeeRate(data.monthly_rate_pct ?? 3.8);
      setTierMonths(data.tier_months ?? 6);
      setMaxTiers(data.max_tiers ?? 6);
      setEmailSubject(`Your Instabridge funding has been approved – ${data.name}`);
      setEmailBody(`Dear ${data.name},\n\nWe're delighted to confirm your pre-settlement funding has been approved for ${formatCurrency(data.funding_recommendation || data.requested_amount)}. Funds will be disbursed within 24 hours.\n\nBest,\nInstabridge Underwriting`);
    } catch { toast.error("Failed to load application"); }
  };

  const updateStatus = async (status) => {
    // Intercept funded — never let a status flip happen without a Loan.
    // Opens the Fund Case dialog which owns the full transition.
    if (status === "funded") {
      setFundOpen(true);
      return;
    }
    try {
      await api.patch(`/applications/${id}`, { status });
      toast.success(`Moved to ${STATUS_LABELS[status]}`);
    } catch (e) { toast.error(errMsg(e, "Failed to update status")); }
    load();  };

  const saveRecommendation = async () => {
    await api.patch(`/applications/${id}`, { funding_recommendation: Number(recommendation) || 0 });
    toast.success("Recommendation saved");
    load();
  };

  const saveFeeTerms = async () => {
    const rate = Number(feeRate);
    const tier = Number(tierMonths);
    const tiers = Number(maxTiers);
    if (!(rate > 0) || !(tier > 0) || !(tiers > 0)) {
      toast.error("All fee fields must be positive numbers");
      return;
    }
    await api.patch(`/applications/${id}`, {
      monthly_rate_pct: rate,
      tier_months: tier,
      max_tiers: tiers,
    });
    toast.success("Fee terms updated");
    load();
  };

  const resetFeeTerms = () => {
    setFeeRate(3.8); setTierMonths(6); setMaxTiers(6);
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/applications/${id}/notes`, { author: admin.user.name, role: "admin", content: note.trim() });
    setNote("");
    toast.success("Note added");
    load();
  };

  const uploadAgreement = async () => {
    if (!agreementName.trim()) return;
    await api.post(`/applications/${id}/documents`, {
      name: agreementName.trim().endsWith(".pdf") ? agreementName.trim() : `${agreementName.trim()}.pdf`,
      category: "agreement", uploaded_by: "admin",
      size_kb: Math.floor(Math.random() * 800) + 200,
    });
    setAgreementName("");
    toast.success("Agreement uploaded");
    load();
  };

  const deleteDoc = async (docId) => {
    await api.delete(`/applications/${id}/documents/${docId}`);
    toast.success("Document removed");
    load();
  };

  const sendApproval = async () => {
    try {
      await api.post(`/applications/${id}/send-approval`, { subject: emailSubject, body: emailBody });
      toast.success("Approval email sent (simulated)");
    } catch (e) { toast.error(errMsg(e, "Failed to send approval")); }
    load();
  };

  if (!app) {
    return <div className="min-h-screen flex items-center justify-center text-[#6B7280]">Loading…</div>;
  }

  return (
    <div className="bg-[#F9FAFB] min-h-full" data-testid="admin-app-detail">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-8 py-8 grid lg:grid-cols-3 gap-6">
        {/* LEFT: details */}
        <div className="lg:col-span-2 space-y-6">
          <VerificationBanner application={app} onResolved={load} />
          <ServiceAreaReviewBanner application={app} onResolved={load} />
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-7">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="uppercase-label">Application</div>
                <h1 className="font-display text-3xl text-[#0A2540] font-semibold mt-1.5">{app.name}</h1>
                <div className="text-sm text-[#6B7280] mt-1">{app.email} · {app.phone}</div>
                <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="app-case-type-row">
                  <span className={`inline-flex items-center px-2 h-5 text-[10px] uppercase tracking-wider rounded border ${
                    (app.case_type || "auto_accident") === "workers_comp"
                      ? "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]"
                      : "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]"
                  }`}>
                    {(app.case_type || "auto_accident") === "workers_comp" ? "Workers' Compensation" : "Auto Accident"}
                  </span>
                  <Select
                    value={app.case_type || "auto_accident"}
                    onValueChange={async (v) => {
                      try {
                        await api.patch(`/applications/${id}`, { case_type: v });
                        toast.success("Case type updated");
                        load();
                      } catch { toast.error("Failed to update case type"); }
                    }}
                  >
                    <SelectTrigger className="h-7 w-[200px] text-xs" data-testid="admin-case-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto_accident">Auto Accident</SelectItem>
                      <SelectItem value="workers_comp">Workers&apos; Compensation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="text-right">
                <StatusBadge status={app.status} />
                <div className="font-display text-3xl font-semibold text-[#0066FF] mt-3 tabular-nums">{formatCurrency(app.requested_amount)}</div>
                <div className="text-xs text-[#6B7280]">requested</div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5 mt-7 pt-7 border-t border-[#E5E7EB]">
              {(app.case_type || "auto_accident") === "workers_comp" ? (
                <>
                  <KV label="Date of injury" value={formatDate(app.accident_date)} />
                  <KV label="Employer" value={app.employer_name || "—"} />
                  <KV label="Body parts injured" value={app.body_parts_injured || "—"} />
                  <KV label="Receiving WC benefits?" value={
                    { yes: "Yes", no: "No", unsure: "Unsure" }[app.currently_receiving_benefits] || "—"
                  } />
                  <KV label="Has attorney?" value={
                    app.has_attorney === true ? "Yes" : app.has_attorney === false ? "No" : "—"
                  } />
                </>
              ) : (
                <>
                  <KV label="Accident type" value={app.accident_type} />
                  <KV label="Accident date" value={formatDate(app.accident_date)} />
                </>
              )}
              <KV label="Attorney" value={`${app.attorney_name}${app.attorney_firm ? ` (${app.attorney_firm})` : ""}`} />
              <KV label="Attorney contact" value={`${app.attorney_email} · ${app.attorney_phone}`} />
              <KV label="Submitted" value={formatDate(app.created_at)} />
              <KV label="Attorney verified" value={app.attorney_verified ? "Yes" : "Pending"} />
              <KV label="Claim filed in" value={app.claim_state === "UNSURE" ? "Not sure" : app.claim_state || "—"} />
              {app.date_of_birth && <KV label="Date of birth" value={formatDate(app.date_of_birth)} />}
              {app.ssn && <KV label="SSN (last 4)" value={`***-**-${app.ssn.replace(/\D/g, "").slice(-4)}`} />}
              {(app.street_address || app.city) && <KV label="Address" value={`${app.street_address || ""}${app.city ? `, ${app.city}` : ""}${app.state ? `, ${app.state}` : ""}${app.zip_code ? ` ${app.zip_code}` : ""}`} />}
              {(app.case_type || "auto_accident") !== "workers_comp" && app.insurance_company && <KV label="Insurance company" value={app.insurance_company} />}
              {(app.case_type || "auto_accident") !== "workers_comp" && app.policy_limits && <KV label="Policy limits" value={app.policy_limits} />}
              {app.has_prior_funding && <KV label="Prior funding" value={`${app.prior_funding_company || "—"} · $${(app.prior_funding_amount || 0).toLocaleString()}`} />}
            </div>
            {app.accident_description && (
              <div className="mt-7 pt-7 border-t border-[#E5E7EB]">
                <div className="uppercase-label mb-2">What happened</div>
                <p className="text-[#374151] leading-relaxed">{app.accident_description}</p>
              </div>
            )}
            <div className="mt-7 pt-7 border-t border-[#E5E7EB]">
              <div className="uppercase-label mb-2">Injury description</div>
              <p className="text-[#374151] leading-relaxed">{app.injury_description}</p>
            </div>
            {app.client_signature_data && (
              <div className="mt-7 pt-7 border-t border-[#E5E7EB]">
                <div className="uppercase-label mb-2">Claimant signature</div>
                <div className="flex items-start gap-4 flex-wrap">
                  <img src={app.client_signature_data} alt="Claimant signature" className="border border-[#E5E7EB] rounded p-2 bg-[#F9FAFB] max-h-24" />
                  <div className="text-sm text-[#0A2540]">{app.client_signature_name}</div>
                </div>
              </div>
            )}

            <div className="mt-7 pt-7 border-t border-[#E5E7EB]">
              <div className="uppercase-label mb-3">Attorney portal link</div>
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-4 flex items-center gap-3 flex-wrap">
                <code className="flex-1 text-xs text-[#0A2540] font-mono break-all min-w-0" data-testid="attorney-link-display">
                  {window.location.origin}/attorney/{app.attorney_token}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const link = `${window.location.origin}/attorney/${app.attorney_token}`;
                    navigator.clipboard.writeText(link);
                    toast.success("Link copied to clipboard");
                  }}
                  data-testid="copy-attorney-link-btn"
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link
                </Button>
                <a href={`mailto:${app.attorney_email}?subject=${encodeURIComponent(`Verification request: ${app.name} - Instabridge Funding`)}&body=${encodeURIComponent(`Hi ${app.attorney_name},\n\nYour client ${app.name} has applied for pre-settlement funding through Instabridge. Please use the secure link below to verify representation, sign the acknowledgment, and upload supporting documents:\n\n${window.location.origin}/attorney/${app.attorney_token}\n\nNo account or login is required. The link is unique to this case.\n\nThanks,\nInstabridge Underwriting`)}`}>
                  <Button variant="outline" size="sm" data-testid="email-attorney-btn">
                    <Mail className="w-3.5 h-3.5 mr-1.5" /> Email attorney
                  </Button>
                </a>
                <a href={`/attorney/${app.attorney_token}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" data-testid="preview-attorney-link-btn">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Preview
                  </Button>
                </a>
              </div>
              <p className="text-xs text-[#6B7280] mt-2">
                "Email attorney" opens your default mail client with the link pre-filled. The link works without an account — perfect for first-time verification.
              </p>
            </div>
          </div>

          {/* Tabs: docs, notes, emails */}
          <Tabs defaultValue="documents" className="bg-white border border-[#E5E7EB] rounded-2xl">
            <TabsList className="px-3 pt-3 bg-transparent border-b border-[#E5E7EB] rounded-none w-full justify-start gap-1">
              <TabsTrigger value="documents" data-testid="tab-documents">Documents ({app.documents.length})</TabsTrigger>
              <TabsTrigger value="notes" data-testid="tab-notes">Notes ({app.notes.length})</TabsTrigger>
              <TabsTrigger value="emails" data-testid="tab-emails">Emails ({app.emails.length})</TabsTrigger>
              <TabsTrigger value="communications" data-testid="tab-communications">Communications</TabsTrigger>
              <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
            </TabsList>

            <TabsContent value="documents" className="p-6 space-y-4">
              <div className="space-y-2">
                {app.documents.length === 0 && <div className="text-sm text-[#6B7280] italic">No documents yet.</div>}
                {app.documents.map((d) => (
                  <div key={d.id} className="flex items-center justify-between p-3 border border-[#E5E7EB] rounded-lg" data-testid={`doc-${d.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded bg-[#F9FAFB] flex items-center justify-center"><FileText className="w-4 h-4 text-[#0066FF]" /></div>
                      <div>
                        <div className="text-sm font-medium text-[#0A2540]">{d.name}</div>
                        <div className="text-xs text-[#6B7280]">{d.category.replace("_", " ")} · {(d.size_kb/1024).toFixed(2)} MB · {d.uploaded_by}</div>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => deleteDoc(d.id)} data-testid={`del-doc-${d.id}`}><Trash2 className="w-4 h-4 text-[#B91C1C]" /></Button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-2 border-t border-[#E5E7EB]">
                <Input placeholder="Agreement filename (e.g., funding_agreement.pdf)" value={agreementName} onChange={(e) => setAgreementName(e.target.value)} data-testid="agreement-name-input" />
                <Button onClick={uploadAgreement} className="btn-primary-navy" data-testid="upload-agreement-btn"><Upload className="w-4 h-4 mr-2" />Upload</Button>
              </div>
            </TabsContent>

            <TabsContent value="notes" className="p-6 space-y-4">
              <div className="space-y-3">
                {app.notes.length === 0 && <div className="text-sm text-[#6B7280] italic">No internal notes yet.</div>}
                {app.notes.map((n) => (
                  <div key={n.id} className="p-4 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]" data-testid={`note-${n.id}`}>
                    <div className="flex items-center justify-between text-xs text-[#6B7280] mb-1.5">
                      <span className="font-medium text-[#0A2540]">{n.author} · {n.role}</span>
                      <span>{formatDate(n.created_at)}</span>
                    </div>
                    <div className="text-sm text-[#374151] whitespace-pre-wrap">{n.content}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-2 pt-2 border-t border-[#E5E7EB]">
                <Textarea placeholder="Add internal note (visible to underwriting team only)…" value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="new-note-input" />
                <Button onClick={addNote} className="btn-primary-navy" data-testid="add-note-btn"><Plus className="w-4 h-4 mr-2" />Add note</Button>
              </div>
            </TabsContent>

            <TabsContent value="emails" className="p-6 space-y-3">
              {app.emails.length === 0 && <div className="text-sm text-[#6B7280] italic">No emails sent yet.</div>}
              {app.emails.map((e) => (
                <div key={e.id} className="p-4 rounded-lg border border-[#E5E7EB]" data-testid={`email-${e.id}`}>
                  <div className="flex items-center gap-2 text-xs text-[#6B7280] mb-1.5">
                    <Mail className="w-3.5 h-3.5" /> To: {e.to} · {formatDate(e.sent_at)}
                  </div>
                  <div className="font-medium text-[#0A2540]">{e.subject}</div>
                  <div className="text-sm text-[#374151] mt-1 whitespace-pre-wrap">{e.body}</div>
                </div>
              ))}
            </TabsContent>
            <TabsContent value="communications" className="p-6">
              <CommunicationsTab applicationId={app.id} contactEmail={app.email} attorneyEmail={app.attorney_email} />
            </TabsContent>
            <TabsContent value="timeline" className="p-6">
              <ActivityTimeline applicationId={app.id} attorneyEmail={app.attorney_email} entityType="application" />
            </TabsContent>
          </Tabs>
        </div>

        {/* RIGHT: actions */}
        <div className="space-y-6">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
            <div className="uppercase-label mb-3">Applicant flags</div>
            <label className="flex items-start gap-2 cursor-pointer text-sm text-[#0A2540] select-none"
                   data-testid="online-lead-label">
              <input
                type="checkbox"
                checked={!!app.online_lead}
                onChange={async (e) => {
                  const next = e.target.checked;
                  setApp((a) => ({ ...a, online_lead: next }));
                  try {
                    await api.patch(`/applications/${id}`, { online_lead: next });
                    toast.success(next ? "Marked as Online Lead" : "Removed Online Lead");
                  } catch (err) {
                    setApp((a) => ({ ...a, online_lead: !next }));
                    toast.error("Update failed");
                  }
                }}
                data-testid="online-lead-checkbox"
                className="mt-0.5 rounded border-[#E5E7EB]"
              />
              <span>
                <b>Online Lead</b>
                <span className="block text-xs text-[#6B7280] mt-0.5">
                  Applicant originated from an online source. Appears in the
                  Online Applications dashboard.
                </span>
              </span>
            </label>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
            <div className="uppercase-label mb-3">Update status</div>
            <Select value={app.status} onValueChange={updateStatus}>
              <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_ORDER.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`status-opt-${s}`}>{STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {app.status === "approved" && !app.funded_loan_id ? (
              <Button
                onClick={() => setFundOpen(true)}
                disabled={["pending", "ineligible"].includes(app.service_area_review?.status)}
                title={["pending", "ineligible"].includes(app.service_area_review?.status) ? "Service-area review must be cleared before funding" : undefined}
                className="w-full mt-4 bg-[#059669] hover:bg-[#047857] text-white font-medium"
                data-testid="fund-case-btn"
              >
                Fund this case
              </Button>
            ) : null}
            {app.funded_loan_id ? (
              <div className="mt-4 text-xs rounded-md bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46] px-3 py-2" data-testid="funded-loan-indicator">
                Funded as loan <strong>{app.funded_loan_id}</strong>
              </div>
            ) : null}

            <div className="mt-6">
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Funding recommendation</Label>
              <div className="flex gap-2 mt-1.5">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]">$</span>
                  <Input type="number" value={recommendation} onChange={(e) => setRecommendation(e.target.value)} className="pl-7" data-testid="recommendation-input" />
                </div>
                <Button onClick={saveRecommendation} className="btn-primary-navy" data-testid="save-recommendation-btn">Save</Button>
              </div>
              <div className="text-xs text-[#6B7280] mt-1.5">Requested: {formatCurrency(app.requested_amount)}</div>
            </div>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6" data-testid="fee-terms-block">
            <div className="flex items-center justify-between mb-3">
              <div className="uppercase-label">Fee terms</div>
              <button type="button" onClick={resetFeeTerms} className="text-xs text-[#0066FF] hover:underline" data-testid="fee-reset-btn">Reset to defaults</button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Monthly compounding rate</Label>
                <div className="relative mt-1.5">
                  <Input type="number" step="0.1" min="0" max="20" value={feeRate} onChange={(e) => setFeeRate(e.target.value)} className="pr-7" data-testid="fee-rate-input" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] text-sm">%</span>
                </div>
                <div className="text-xs text-[#6B7280] mt-1">Standard agreement: 3.8%/mo. Lower rates available per attorney request.</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Tier interval</Label>
                  <div className="relative mt-1.5">
                    <Input type="number" min="1" max="36" value={tierMonths} onChange={(e) => setTierMonths(e.target.value)} className="pr-10" data-testid="tier-months-input" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] text-xs">mo</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Max tiers (cap)</Label>
                  <Input type="number" min="1" max="20" value={maxTiers} onChange={(e) => setMaxTiers(e.target.value)} className="mt-1.5" data-testid="max-tiers-input" />
                </div>
              </div>
              <Button onClick={saveFeeTerms} className="btn-primary-navy w-full" data-testid="save-fee-terms-btn">Save fee terms</Button>
              <div className="text-xs text-[#6B7280] pt-2 border-t border-[#F3F4F6]">
                <div>Cap = {maxTiers && tierMonths ? `${Number(maxTiers) * Number(tierMonths)} months` : "—"}</div>
                <div>6-mo tier fee ≈ {feeRate ? (((1 + Number(feeRate) / 100) ** 6 - 1) * 100).toFixed(2) : "—"}%</div>
                <div>Annual fee ≈ {feeRate ? (((1 + Number(feeRate) / 100) ** 12 - 1) * 100).toFixed(2) : "—"}%</div>
              </div>
              {(app.status === "approved" || app.status === "funded") && (
                <a href={`/attorney/portal/payoff/${app.id}`} target="_blank" rel="noopener noreferrer" className="block">
                  <Button variant="outline" className="w-full" data-testid="admin-view-payoff-btn">View payoff letter →</Button>
                </a>
              )}
            </div>
          </div>


          {app.signature_data && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6" data-testid="signature-block">
              <div className="uppercase-label mb-3">Attorney signature</div>
              <img src={app.signature_data} alt="Attorney signature" className="border border-[#E5E7EB] rounded p-2 bg-[#F9FAFB] max-h-32" />
              <div className="text-sm text-[#0A2540] font-medium mt-2">{app.signature_name}</div>
            </div>
          )}

          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
            <div className="uppercase-label mb-3">Send approval email</div>
            <div className="space-y-3">
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Subject" data-testid="email-subject-input" />
              <Textarea rows={6} value={emailBody} onChange={(e) => setEmailBody(e.target.value)} placeholder="Body" data-testid="email-body-input" />
              <Button onClick={sendApproval} className="w-full btn-primary-navy" data-testid="send-approval-btn"><Send className="w-4 h-4 mr-2" />Send approval (simulated)</Button>
            </div>
          </div>
        </div>
      </div>

      <FundCaseDialog
        appId={id}
        open={fundOpen}
        onOpenChange={setFundOpen}
        onFunded={() => { load(); }}
      />
    </div>
  );
}

const KV = ({ label, value }) => (
  <div>
    <div className="uppercase-label mb-1">{label}</div>
    <div className="text-sm text-[#0A2540]">{value}</div>
  </div>
);
